import type Konva from "konva";
import type { Node as KonvaNode } from "konva/lib/Node";
import { useCounter, useRefHistory } from "@vueuse/core";
import { defineStore } from "pinia";
import { ref } from "vue";

import { rotationCursorBase64 } from "@/telestrations/constants/cursorsBase64";
import { type Shape, ShapeType, SpotlightShapeName } from "@/telestrations/types/canvas";

import { getShapeConfig } from "../constants/shapes";

// Store for managing shapes
export const useShapesStore = defineStore("shapes", () => {
  const toolsStore = useToolsStore();
  const canvasStore = useCanvasStore();
  const shapes = ref<Shape[]>([]);
  const selectedShape = ref<Shape | null>(null);
  const transformerRef = ref<Konva.Transformer | null>(null);
  const transformerPosition = ref<{ x: number; y: number; height: number } | null>(null);
  const isDrawing = ref(false);
  const drawingShape = ref<Shape | null>(null);
  const isTransforming = ref(false);
  const isUpdating = ref(false);
  const transformerConfig = ref<Konva.TransformerConfig>({
    centeredScaling: true,
    centeredRotation: true,
    flipEnabled: false,
    rotateAnchorCursor: rotationCursorBase64,
    padding: 0,
    ignoreStroke: true,
  });
  const { count: shapeId, inc: incrementShapeId } = useCounter(1);

  const historyState = useRefHistory(shapes, {
    deep: true,
  });

  watch(
    () => [canvasStore.stageConfig.width, canvasStore.stageConfig.height],
    () => nextTick(() => updateTransformerPosition())
  );

  function selectShape(shape: Shape | null) {
    selectedShape.value = shape;

    attachTransformerToShape(shape);
    updateTransformerPosition();
    applyTransformerSpotlightAnchorsConfig(shape);
  }

  function applyTransformerSpotlightAnchorsConfig(shape: Shape | null): void {
    if (!transformerRef.value) {
      console.warn("No transformer found");
      return;
    }

    transformerConfig.value.rotateEnabled = shape?.name !== SpotlightShapeName.ELLIPSE || shape.type !== ShapeType.Spotlight;
    transformerConfig.value.enabledAnchors = [
      "top-left",
      "top-right",
      "bottom-left",
      "bottom-right",
      "middle-left",
      "middle-right",
      "top-center",
      "bottom-center",
    ];
  }

  function addShape(shape: Shape) {
    shapes.value.push(shape);
  }

  function removeShape(id: string) {
    shapes.value = shapes.value.filter((shape) => shape.id !== id);

    if (selectedShape.value?.id === id) {
      selectShape(null);
    }
  }

  function clearShapes() {
    shapes.value = [];
    selectShape(null);
    toolsStore.resetToSelectionTool();
  }

  function updateShape(shapeToUpdate: Shape, updates: Partial<Shape>) {
    const shape = shapes.value.find((s) => s.id === shapeToUpdate.id);

    if (shape) {
      Object.assign(shape, updates);
    }
  }

  function updateShapePosition(shapeToUpdate: Shape, x: number, y: number) {
    const shape = shapes.value.find((s) => s.id === shapeToUpdate.id);

    if (!shape) {
      console.warn(`Shape with id ${shapeToUpdate.id} not found`);
      return;
    }

    const currentX = shape.x ?? 0;
    const currentY = shape.y ?? 0;

    // Calculate desired movement delta
    let dx = (x ?? currentX) - currentX;
    let dy = (y ?? currentY) - currentY;

    const stageWidth = canvasStore.stageConfig.width ?? 0;
    const stageHeight = canvasStore.stageConfig.height ?? 0;

    // If we have a transformer attached, use its border rect (nodes' bounds + padding)
    // to clamp movement, explicitly ignoring the rotation handle distance
    const transformerNode = transformerRef.value?.getNode() as Konva.Transformer | undefined;
    if (transformerNode) {
      const nodes = transformerNode.nodes();
      let minX = Number.POSITIVE_INFINITY;
      let minY = Number.POSITIVE_INFINITY;
      let maxX = Number.NEGATIVE_INFINITY;
      let maxY = Number.NEGATIVE_INFINITY;

      if (nodes.length > 0) {
        for (const n of nodes) {
          const r = n.getClientRect({ skipStroke: false, skipShadow: true });
          if (r.x < minX) minX = r.x;
          if (r.y < minY) minY = r.y;
          if (r.x + r.width > maxX) maxX = r.x + r.width;
          if (r.y + r.height > maxY) maxY = r.y + r.height;
        }
      } else {
        // Fallback to transformer's own rect
        const r = transformerNode.getClientRect();
        minX = r.x;
        minY = r.y;
        maxX = r.x + r.width;
        maxY = r.y + r.height;
      }

      const padding = typeof transformerNode.padding === "function" ? transformerNode.padding() : 0;
      const box = {
        x: minX - padding,
        y: minY - padding,
        width: maxX - minX + padding * 2,
        height: maxY - minY + padding * 2,
      };

      // Proposed transformer box after movement
      const proposedLeft = box.x + dx;
      const proposedTop = box.y + dy;
      const proposedRight = proposedLeft + box.width;
      const proposedBottom = proposedTop + box.height;

      // Clamp horizontally
      if (proposedLeft < 0) {
        dx = dx - proposedLeft; // shift right to align to 0
      }
      if (proposedRight > stageWidth) {
        dx = dx - (proposedRight - stageWidth);
      }

      // Clamp vertically
      if (proposedTop < 0) {
        dy = dy - proposedTop; // shift down to align to 0
      }
      if (proposedBottom > stageHeight) {
        dy = dy - (proposedBottom - stageHeight);
      }
    } else {
      // Fallback: clamp using shape's own width/height if transformer is not available
      const width = (shape.width ?? 0) * (shape.scaleX ?? 1);
      const height = (shape.height ?? 0) * (shape.scaleY ?? 1);
      const targetX = currentX + dx;
      const targetY = currentY + dy;
      const clampedX = Math.max(0, Math.min(targetX, Math.max(0, stageWidth - width)));
      const clampedY = Math.max(0, Math.min(targetY, Math.max(0, stageHeight - height)));
      dx = clampedX - currentX;
      dy = clampedY - currentY;
    }

    const nextX = currentX + dx;
    const nextY = currentY + dy;

    if (nextX !== currentX || nextY !== currentY) {
      updateShape(shape, { x: nextX, y: nextY });
      updateTransformerPosition();
    }
  }

  function getShapeNode(shape: Shape) {
    const stage = canvasStore.getKonvaStage();

    if (!stage) {
      console.error("Konva stage not found");
      return null;
    }

    const node = stage.findOne(`#${shape.id}`);

    return node;
  }

  function updateTransformerPosition() {
    if (!selectedShape.value) {
      transformerPosition.value = null;
      return;
    }
    if (!transformerRef.value) {
      transformerPosition.value = null;
      return;
    }

    const transformerNode = transformerRef.value.getNode() as Konva.Transformer;

    const box = transformerNode.getClientRect();

    transformerPosition.value = { x: box.x, y: box.y, height: box.height };
  }

  function startTransforming() {
    isTransforming.value = true;
    startShapeUpdate();
  }

  function stopTransforming() {
    isTransforming.value = false;
    finishShapeUpdate();
  }

  function startShapeUpdate() {
    isUpdating.value = true;
  }

  function finishShapeUpdate() {
    requestAnimationFrame(() => {
      isUpdating.value = false;
    });
  }

  function reorderLayer(id: string, newIndex: number) {
    const shape = shapes.value.find((s) => s.id === id);
    if (shape) {
      shape.layerIndex = newIndex;
      shapes.value.sort((a, b) => a.layerIndex - b.layerIndex);
    }
  }

  function sendShapeToTop(id: string) {
    const index = shapes.value.findIndex((s) => String(s.id) === String(id));
    if (index === -1) {
      console.warn(`No shape found with provided id - '${id}'`);
      return;
    }

    const [shape] = shapes.value.splice(index, 1);
    shapes.value.push(shape); // Move to the top (last in the array)
  }

  function sendShapeToBottom(id: string) {
    const index = shapes.value.findIndex((s) => String(s.id) === String(id));
    if (index === -1) {
      console.warn(`No shape found with provided id - '${id}'`);
      return;
    }

    const [shape] = shapes.value.splice(index, 1);
    shapes.value.unshift(shape); // Move to the bottom (first in the array)
  }

  function handleSelectedShapeDraw(e: Event) {
    selectShape(null);

    if (!toolsStore.selectedTool || toolsStore.selectedTool === "selection") {
      return;
    }

    const selectedShape = toolsStore.selectedTool;

    const node = e.target as KonvaNode | null;

    if (!node) {
      console.warn("No node found");
      return;
    }

    const stage = node.getStage();

    if (!stage) {
      console.warn("No stage found");
      return;
    }

    selectShape(null);

    const pos = stage.getPointerPosition();

    if (!pos) {
      console.warn("No position found");
      return;
    }

    const scale = stage.scaleY() ?? 1;
    const offsetX = stage.offsetX() ?? 0 / scale;
    const offsetY = stage.offsetY() ?? 0 / scale;
    let startX = pos.x / stage.scaleX();
    let startY = pos.y / stage.scaleY();
    // For Text, Arrow, and Line shapes, add offsetX and offsetY to the starting position
    if (selectedShape === ShapeType.Text || selectedShape === ShapeType.Arrow || selectedShape === ShapeType.Line) {
      startX += offsetX;
      startY += offsetY;
    }
    const shape = getShapeConfig(
      {
        id: String(shapeId.value),
        layerIndex: shapes.value.length,
        type: selectedShape,
        startX: selectedShape === ShapeType.Text ? startX + offsetX : startX,
        startY: selectedShape === ShapeType.Text ? startY + offsetY : startY,
      },
      selectedShape === ShapeType.Text ? { isNew: true } : undefined
    );

    addShape(shape);
    incrementShapeId();
    drawingShape.value = shape;
  }

  function onShapeDragStart(e: Event) {
    startShapeUpdate();
    onShapeDragMove(e);
  }

  function onShapeDragMove(e: Event) {
    const node = e.target as KonvaNode | null;

    if (!node) {
      transformerPosition.value = null;
      return;
    }

    updateTransformerPosition();
  }

  function onShapeDragEnd(e: Event, shape: Shape) {
    const node = e.target as KonvaNode | null;

    if (!node) {
      console.warn("No node found");
      return;
    }

    finishShapeUpdate();

    updateShape(shape, { x: node.x(), y: node.y() });
  }

  function onShapeTransformEnd(e: Event) {
    // shape is transformed, let us save new attrs back to the node
    // find element in our state
    const shape = shapes.value.find((s) => s.id === selectedShape.value?.id);

    if (!shape) {
      console.warn("No shape found");
      return;
    }

    const node = e.target as KonvaNode | null;

    if (!node) {
      console.warn("No node found");
      return;
    }

    // update the state
    updateShape(shape, {
      x: node.x(),
      y: node.y(),
      rotation: node.rotation(),
      scaleX: node.scaleX(),
      scaleY: node.scaleY(),
    });
  }

  function attachTransformerToShape(shape: Shape | null) {
    if (!transformerRef.value) {
      console.warn("No transformer found");
      return;
    }

    const transformerNode = transformerRef.value.getNode() as Konva.Transformer;
    const stage = transformerNode.getStage();

    if (!stage) {
      console.warn("No stage found");
      return;
    }

    const selectedNode = stage.findOne(`#${shape?.id}`);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error ....
    // do nothing if selected node is already attached
    if (selectedNode === transformerNode.node()) {
      // console.warn("Transformer already attached");
      return;
    }

    if (selectedNode) {
      // Add padding to position transformer outside the shape's stroke boundary
      const strokeWidth = (selectedNode as Konva.Shape).strokeWidth?.() || 0;
      const padding = strokeWidth / 2;

      // Update transformer configuration for this shape
      transformerNode.padding(padding);

      // attach to another node
      transformerNode.nodes([selectedNode]);
    } else {
      // remove transformer
      transformerNode.nodes([]);
    }
  }

  const $reset = () => {
    shapes.value = [];
    selectedShape.value = null;
    transformerPosition.value = null;
    isDrawing.value = false;
    drawingShape.value = null;
    isTransforming.value = false;
    isUpdating.value = false;

    if (transformerRef.value) {
      const transformer = transformerRef.value.getNode() as Konva.Transformer | null;

      if (transformer) {
        transformer.nodes([]);
      }
    }

    nextTick(() => {
      if (historyState.canUndo.value) {
        historyState.clear();
      }
    });
  };

  async function addShapeOnClick(overrides?: Partial<Shape>) {
    const stage = canvasStore.getKonvaStage();

    if (!stage) {
      console.warn("No stage found");
      return;
    }

    const pos = stage.getPointerPosition();

    if (!pos) {
      console.warn("No position found");
      return;
    }

    if (!toolsStore.selectedTool || toolsStore.selectedTool === "selection") {
      console.warn("No selected tool found");
      return;
    }

    const scaleX = stage.scaleX() ?? 1;
    const scaleY = stage.scaleY() ?? 1;
    const offsetX = stage.offsetX() ?? 0 / scaleX;
    const offsetY = stage.offsetY() ?? 0 / scaleY;
    let startX = pos.x / scaleX + offsetX;
    let startY = pos.y / scaleY + offsetY;

    if (toolsStore.selectedTool === ShapeType.Rectangle && overrides?.width && overrides?.height) {
      // Adjust position for rectangle to be centered
      startX = startX - overrides.width / 2;
      startY = startY - overrides.height / 2;
    }

    // Draw a shape on click
    const shape = getShapeConfig(
      {
        id: String(shapeId.value),
        layerIndex: shapes.value.length,
        type: toolsStore.selectedTool,
        startX,
        startY,
      },
      overrides
    );
    addShape(shape);

    await nextTick();
    selectShape(shape);
    incrementShapeId();
    isDrawing.value = false;
    drawingShape.value = null;
    toolsStore.resetToSelectionTool();
  }

  return {
    $reset,
    shapes,
    selectedShape,
    isDrawing,
    drawingShape,
    isTransforming,
    isUpdating,
    transformerRef,
    transformerConfig,
    transformerPosition,
    sendShapeToTop,
    sendShapeToBottom,
    selectShape,
    addShape,
    removeShape,
    clearShapes,
    updateShape,
    updateShapePosition,
    updateTransformerPosition,
    startTransforming,
    stopTransforming,
    getShapeNode,
    historyState,
    reorderLayer,
    onShapeDragStart,
    onShapeDragMove,
    onShapeDragEnd,
    onShapeTransformEnd,
    handleSelectedShapeDraw,
    attachTransformerToShape,
    applyTransformerSpotlightAnchorsConfig,
    shapeId,
    incrementShapeId,
    addShapeOnClick,
  };
});
